import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  STEP_TYPES,
  countDependentSteps,
  endSlot,
  ensureSequenceShape,
  formatStepCanvasLabel,
  removeStep,
  setSlotTarget,
  sortBranchesForDisplay,
  stepKey,
  stepNeedsAttention,
} from "./sequenceUtils";
import {
  AddButton,
  BranchSplit,
  EndNode,
  JumpNode,
  OpenSlot,
  StartNode,
  StepCard,
  VLine,
} from "./SequenceNodes";
import { FitViewIcon, ZoomInIcon, ZoomOutIcon } from "./SequenceIcons";

const MIN_SCALE = 0.25;
const MAX_SCALE = 1.5;
const TOP_PADDING = 32;

const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/**
 * Pan / zoom viewport. The view is anchored on the content's horizontal centre so the
 * tree stays put while branches grow on either side.
 */
function useViewport() {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ cx: null, y: TOP_PADDING, scale: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const measure = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (view.cx === null && viewportRef.current) {
      setView((v) => ({ ...v, cx: viewportRef.current.clientWidth / 2 }));
    }
  }, [view.cx]);

  const zoomAt = useCallback((factor, px, py) => {
    setView((v) => {
      const scale = clampScale(v.scale * factor);
      const ratio = scale / v.scale;
      const { w } = sizeRef.current;
      const left = v.cx - (w * v.scale) / 2;
      const nextLeft = px - (px - left) * ratio;
      return {
        scale,
        cx: nextLeft + (w * scale) / 2,
        y: py - (py - v.y) * ratio,
      };
    });
  }, []);

  const zoomCenter = useCallback(
    (factor) => {
      const vp = viewportRef.current;
      if (!vp) return;
      zoomAt(factor, vp.clientWidth / 2, vp.clientHeight / 2);
    },
    [zoomAt],
  );

  const fitView = useCallback(() => {
    const vp = viewportRef.current;
    const { w, h } = sizeRef.current;
    if (!vp || !w || !h) return;
    const scale = clampScale(
      Math.min(1, (vp.clientWidth - 64) / w, (vp.clientHeight - TOP_PADDING * 2) / h),
    );
    setView({ scale, cx: vp.clientWidth / 2, y: TOP_PADDING });
  }, []);

  // Wheel: scroll pans, ctrl/cmd + wheel (and trackpad pinch) zooms.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        const rect = vp.getBoundingClientRect();
        zoomAt(Math.exp(-event.deltaY * 0.0025), event.clientX - rect.left, event.clientY - rect.top);
      } else {
        setView((v) => ({ ...v, cx: v.cx - event.deltaX, y: v.y - event.deltaY }));
      }
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // Drag empty canvas to pan.
  const dragRef = useRef(null);
  const onPointerDown = (event) => {
    if (event.button !== 0 || event.target.closest("[data-nopan],button,input,select,textarea")) {
      return;
    }
    dragRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setView((v) => ({ ...v, cx: v.cx + dx, y: v.y + dy }));
  };
  const onPointerUp = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const left = (view.cx ?? 0) - (size.w * view.scale) / 2;

  return {
    viewportRef,
    contentRef,
    view,
    left,
    ready: view.cx !== null,
    zoomCenter,
    fitView,
    panHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}

const EDITABLE_TYPES = new Set([
  STEP_TYPES.CONNECTION_REQUEST,
  STEP_TYPES.MESSAGE,
  STEP_TYPES.IN_MAIL,
  STEP_TYPES.VOICE_NOTE,
  STEP_TYPES.LIKE_POST,
]);

/** Whether a step has a configuration drawer (others only carry a delay). */
export function isStepEditable(step) {
  return EDITABLE_TYPES.has(step?.type);
}

/**
 * Visual sequence builder: a top-down tree where every path can be extended,
 * branched, or ended independently.
 */
export default function SequenceCanvas({
  steps = [],
  stepStats,
  readOnly = false,
  onChange,
  onNodeClick,
  onAddStep,
  onEditStep,
  className = "",
}) {
  const shaped = useMemo(() => ensureSequenceShape(steps), [steps]);
  const byId = useMemo(() => new Map(shaped.map((s) => [s.id, s])), [shaped]);

  const statsById = useMemo(() => {
    const map = new Map();
    (stepStats || []).forEach((s) => map.set(s.stepId || String(s.stepIndex), s));
    // Stats may be keyed by position in the original list.
    const result = new Map();
    (steps || []).filter(Boolean).forEach((step, index) => {
      const stats = (step.id && map.get(step.id)) || map.get(String(index));
      if (stats) result.set(stepKey(step, index), stats);
    });
    return result;
  }, [stepStats, steps]);

  const { viewportRef, contentRef, view, left, ready, zoomCenter, fitView, panHandlers } =
    useViewport();

  const emit = useCallback(
    (next) => {
      if (!readOnly) onChange?.(next);
    },
    [onChange, readOnly],
  );

  const handleDelete = useCallback(
    (step) => {
      const extra = countDependentSteps(shaped, step.id);
      if (
        extra > 0
        && !window.confirm(
          `Deleting "${formatStepCanvasLabel(step)}" will also remove ${extra} step${
            extra === 1 ? "" : "s"
          } on its other path. Continue?`,
        )
      ) {
        return;
      }
      emit(removeStep(shaped, step.id));
    },
    [shaped, emit],
  );

  const handleDelayChange = useCallback(
    (step, delay) => {
      emit(shaped.map((s) => (s.id === step.id ? { ...s, ...delay } : s)));
    },
    [shaped, emit],
  );

  const handleEdit = useCallback((step) => onEditStep?.(step), [onEditStep]);

  // Steps already drawn in this render; a second path into one shows a "continues to" chip.
  const drawn = new Set();

  const renderSlot = (parentId, condition, targetId) => {
    const slot = { parentId, condition };
    const target = targetId ? byId.get(targetId) : null;

    if (!target) {
      return readOnly ? (
        <>
          <VLine height={28} />
          <EndNode />
        </>
      ) : (
        <>
          <VLine height={20} />
          <OpenSlot onAdd={() => onAddStep?.(slot)} onEnd={() => emit(endSlot(shaped, slot))} />
        </>
      );
    }

    const insert = readOnly ? (
      <VLine height={40} />
    ) : (
      <>
        <VLine height={14} />
        <AddButton onClick={() => onAddStep?.(slot)} />
        <VLine height={14} />
      </>
    );

    if (target.type === STEP_TYPES.END) {
      return (
        <>
          {insert}
          <EndNode onRemove={readOnly ? null : () => emit(setSlotTarget(shaped, slot, null))} />
        </>
      );
    }

    if (target.type === "START" || drawn.has(target.id)) {
      return (
        <>
          {insert}
          <JumpNode label={formatStepCanvasLabel(target)} />
        </>
      );
    }

    drawn.add(target.id);
    return (
      <>
        {insert}
        {renderStep(target)}
      </>
    );
  };

  const renderStep = (step) => {
    const card = (
      <StepCard
        step={step}
        badge={statsById.get(step.id)?.completed}
        readOnly={readOnly}
        needsAttention={!readOnly && stepNeedsAttention(step)}
        canEdit={isStepEditable(step)}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDelayChange={handleDelayChange}
        onClick={onNodeClick}
      />
    );

    if (step.branches?.length) {
      return (
        <>
          {card}
          <VLine height={22} />
          <BranchSplit
            items={sortBranchesForDisplay(step.branches).map((branch) => ({
              key: branch.condition,
              condition: branch.condition,
              content: renderSlot(step.id, branch.condition, branch.targetStepId),
            }))}
          />
        </>
      );
    }

    return (
      <>
        {card}
        {renderSlot(step.id, null, step.nextStepId)}
      </>
    );
  };

  const start = shaped.find((s) => s.type === "START");
  if (start) drawn.add(start.id);

  const gridSize = 16 * view.scale;

  return (
    <div
      ref={viewportRef}
      {...panHandlers}
      className={`relative w-full h-full min-h-[420px] overflow-hidden bg-white cursor-grab active:cursor-grabbing select-none ${className}`}
      style={{
        backgroundImage: "radial-gradient(#d4d9e1 1px, transparent 1px)",
        backgroundSize: `${gridSize}px ${gridSize}px`,
        backgroundPosition: `${left}px ${view.y}px`,
      }}
    >
      <div
        ref={contentRef}
        className="absolute top-0 left-0 w-max flex flex-col items-center pb-16 cursor-default"
        style={{
          transform: `translate(${left}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: "0 0",
          visibility: ready ? "visible" : "hidden",
        }}
      >
        <StartNode />
        {start ? renderSlot(start.id, null, start.nextStepId) : null}
      </div>

      <div className="absolute left-3 bottom-3 flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={() => zoomCenter(1 / 1.2)}
          title="Zoom out"
          className="w-7 h-7 rounded-md text-gray-500 hover:bg-gray-100 flex items-center justify-center"
        >
          <ZoomOutIcon size={16} />
        </button>
        <span className="w-10 text-center text-xs text-gray-500 tabular-nums">
          {Math.round(view.scale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => zoomCenter(1.2)}
          title="Zoom in"
          className="w-7 h-7 rounded-md text-gray-500 hover:bg-gray-100 flex items-center justify-center"
        >
          <ZoomInIcon size={16} />
        </button>
        <button
          type="button"
          onClick={fitView}
          title="Fit to screen"
          className="w-7 h-7 rounded-md text-gray-500 hover:bg-gray-100 flex items-center justify-center"
        >
          <FitViewIcon size={16} />
        </button>
      </div>
    </div>
  );
}
